import { motion } from "framer-motion";
import { Linkedin, GraduationCap, Trophy } from "lucide-react";
import { useSeo } from "@/components/Layout";
import { RevealLines, Reveal, RevealHeading, Stagger, StaggerItem } from "@/components/motion-primitives";
import { CtaButton, SectionLabel } from "@/components/ui-blocks";
import { PhotoCarousel } from "@/components/PhotoCarousel";
import { SITE, IMAGES } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

export default function APropos() {
  const { t } = useLang();
  const a = t.about;
  useSeo(a.seoTitle, a.seoDesc);
  return (
    <div data-testid="a-propos-page">
      {/* 1. QUI JE SUIS — hero 2-col */}
      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 pt-32 lg:pt-32 pb-12 lg:pb-16 grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
        <div className="lg:col-span-6">
          <SectionLabel>{a.label}</SectionLabel>
          <RevealLines as="h1" delay={0.1} className="display-hero text-ink mt-4" lines={a.heroLines} />
          <Reveal delay={0.4}><p className="mt-3 font-display text-lg font-medium text-ink">{a.role}</p></Reveal>
          <Reveal delay={0.45}><p className="mt-5 text-muted-foreground leading-relaxed">{a.who}</p></Reveal>
          <Reveal delay={0.5}>
            <a href={SITE.linkedin} target="_blank" rel="noopener noreferrer" data-testid="about-linkedin"
              className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-ink hover:text-accentbrand transition-colors">
              <Linkedin size={18} className="text-accentbrand" /> {t.common.linkedin}
            </a>
          </Reveal>
        </div>
        <motion.div className="lg:col-span-6" initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}>
          <div className="relative aspect-[4/5] lg:aspect-[5/6] overflow-hidden bg-secondary max-w-md mx-auto lg:max-w-none">
            <img src={IMAGES.portrait} alt={a.imgAlt} className="w-full h-full object-cover object-center" />
          </div>
        </motion.div>
      </section>

      {/* 2. FORMATIONS + 3. EXPÉRIENCE — 2 colonnes distinctes */}
      <section className="bg-secondary/50 border-y border-border">
        <div className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-12 lg:py-20 grid md:grid-cols-2 gap-8 lg:gap-16">
          <div className="md:pr-8 md:border-r md:border-border">
            <div className="flex items-center gap-3 text-accentbrand mb-2"><GraduationCap size={22} /><SectionLabel>{a.diplomasHeading}</SectionLabel></div>
            <Stagger className="mt-4" gap={0.07}>
              {a.diplomas.map((s, i) => (
                <StaggerItem key={i} className="flex items-start gap-4 py-3.5 border-t border-border">
                  <span className="font-mono-alt text-xs text-accentbrand pt-1">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-lg font-semibold text-ink">{s}</span>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
          <div>
            <div className="flex items-center gap-3 text-accentbrand mb-2"><Trophy size={22} /><SectionLabel>{a.expHeading}</SectionLabel></div>
            <Stagger className="mt-4" gap={0.07}>
              {a.experience.map((s, i) => (
                <StaggerItem key={i} className="flex items-start gap-4 py-3.5 border-t border-border">
                  <span className="font-mono-alt text-xs text-accentbrand pt-1">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-lg font-semibold text-ink">{s}</span>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* PRÉPARATION PHYSIQUE */}
      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-10 lg:py-16">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          <div className="lg:col-span-5">
            <SectionLabel>{a.prepaLabel}</SectionLabel>
            <RevealHeading as="h2" text={a.prepaHeading} className="mt-1 display-2 text-ink" />
            <Reveal delay={0.1}><p className="mt-3 text-muted-foreground leading-relaxed">{a.prepaText}</p></Reveal>
            <Reveal delay={0.15}><p className="mt-4 font-mono-alt text-[11px] uppercase tracking-wider text-muted-foreground">{a.prepaCaption}</p></Reveal>
          </div>
          <Reveal className="lg:col-span-7">
            <PhotoCarousel
              testId="prepa-carousel"
              aspect="aspect-[4/3]"
              interval={2500}
              slides={[
                { src: IMAGES.prepaWalkin, alt: a.prepaTeamAlt },
                { src: IMAGES.prepaLadder, alt: a.prepaHuddleAlt },
                { src: IMAGES.prepaTeam, alt: a.prepaTeamAlt },
                { src: IMAGES.prepaHuddle, alt: a.prepaHuddleAlt },
                { src: IMAGES.prepaSolo, alt: a.prepaSoloAlt },
              ]}
            />
          </Reveal>
        </div>
      </section>

      {/* 4. MON APPROCHE */}
      <section className="bg-night text-white">
        <div className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-12 lg:py-20">
          <RevealHeading as="h2" text={a.approachHeading} className="display-1 mb-8" />
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" gap={0.07}>
            {a.values.map((v, i) => (
              <StaggerItem key={i} className="border-t-2 border-accentbrand pt-4">
                <span className="font-mono-alt text-[11px] text-accentbrand">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-2 font-display text-xl font-semibold">{v.title}</h3>
                <p className="mt-1.5 text-sm text-white/70 leading-relaxed">{v.text}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-12 lg:py-20 text-center">
        <RevealHeading as="h2" text={t.home.finalHeading} className="display-1 text-ink" />
        <Reveal delay={0.1} className="mt-7 flex justify-center"><CtaButton to="/contact" data-testid="apropos-cta" className="w-full sm:w-auto justify-center">{t.common.meContacter}</CtaButton></Reveal>
      </section>
    </div>
  );
}
